import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import type {
  BookmarkMutation,
  BookmarkMutationVariables,
  GetShowBookmarkStateQuery,
  GetShowBookmarkStateQueryVariables,
  UnbookmarkMutation,
  UnbookmarkMutationVariables,
} from "@/codegen/graphql/generated/graphql";
import { BOOKMARK } from "@/codegen/graphql/mutations/Bookmark.mutation";
import { UNBOOKMARK } from "@/codegen/graphql/mutations/Unbookmark.mutation";
import { GET_SHOW_BOOKMARK_STATE } from "@/codegen/graphql/queries/GetShowBookmarkState.query";
import { useAuthStore, useIsLoggedIn } from "@/stores/useAuth";
import { useAppStateStore } from "@/stores/useAppState";
import { useTracking } from "@/components/ClientAnalytics";
import { AmplitudeTrackingEvent } from "@/types/amplitude-tracking.type";

export type BookmarkOrigin = "featured" | "player" | "series details";

/**
 * Shared save/unsave wiring for the hero "+ My list" button and the player
 * bookmark icon. Gates on a real account (not just an anonymous device
 * token), de-dupes a double-tap while a mutation is in flight, and fires the
 * matching `save series` / `unsave series` Amplitude event after the mutation
 * resolves (not on intent) so a failed call never reports a save.
 */
export default function useBookmark(
  show: { id: string; title: string; isBookmarked?: boolean },
  origin: BookmarkOrigin,
) {
  // `isBookmarked` is @authenticated on the API, so who fetches it matters.
  // The catalog passes it in: its sections fetch it behind `$withBookmarkState`,
  // from a component, once boot holds a token. The player leaves it undefined
  // and the hook fetches it here, for the same reason: `GetShow` must never
  // select an authenticated field, because the episode route loader runs it
  // before boot has registered the device user — a fresh visitor's deep link
  // then 401s the whole show (non-null propagation), the 401 recovery reloads,
  // and the second load lands on the global error page. This hook only runs
  // inside the root gate, where a token exists.
  const { data } = useQuery<
    GetShowBookmarkStateQuery,
    GetShowBookmarkStateQueryVariables
  >(GET_SHOW_BOOKMARK_STATE, {
    variables: { showId: show.id },
    skip: show.isBookmarked !== undefined || !show.id,
  });
  const isBookmarkedProp =
    show.isBookmarked ?? data?.show.isBookmarked ?? false;

  const isLoggedIn = useIsLoggedIn();
  const openLoginModal = useAppStateStore((s) => s.openLoginModal);
  const { trackAmplitudeEvent } = useTracking();
  const pendingRef = useRef(false);
  const [pending, setPending] = useState(false);
  // Our own last-known write, pending confirmation from the `show.isBookmarked`
  // prop — `null` when there's none, meaning defer to the prop entirely.
  //
  // Why not just resync this from the prop every render (what the previous
  // version did)? A re-render can land with a still-stale prop for reasons
  // unrelated to Apollo's cache broadcast for THIS mutation — e.g. `useMutation`
  // itself re-renders its owner around settlement — and a plain "always copy
  // the prop" resync would clobber the value `save()` just set, right back to
  // stale, before the real broadcast ever arrives. Instead, only give control
  // back to the prop once it has genuinely caught up to match what we wrote:
  // any intermediate render with a still-different prop is a false alarm we
  // correctly ignore.
  const localOverrideRef = useRef<boolean | null>(null);
  // In an effect rather than the render body: mutating a ref during render is a
  // purity violation. Keyed on the prop, which is the only thing that can
  // release the override — so this runs strictly when it can change something.
  useEffect(() => {
    if (
      localOverrideRef.current !== null &&
      isBookmarkedProp === localOverrideRef.current
    ) {
      localOverrideRef.current = null;
    }
  }, [isBookmarkedProp]);

  const [bookmark] = useMutation<BookmarkMutation, BookmarkMutationVariables>(
    BOOKMARK,
  );
  const [unbookmark] = useMutation<
    UnbookmarkMutation,
    UnbookmarkMutationVariables
  >(UNBOOKMARK);

  const save = async () => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);

    const currentlyBookmarked = localOverrideRef.current ?? isBookmarkedProp;

    try {
      if (currentlyBookmarked) {
        await unbookmark({ variables: { ids: [show.id] } });
        localOverrideRef.current = false;
        trackAmplitudeEvent(AmplitudeTrackingEvent.unsaveSeries, {
          "series name": show.title,
          origin,
        });
      } else {
        await bookmark({ variables: { ids: [show.id] } });
        localOverrideRef.current = true;
        trackAmplitudeEvent(AmplitudeTrackingEvent.saveSeries, {
          "series name": show.title,
          origin,
        });
      }
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: "bookmark" } });
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };

  const toggle = () => {
    // Not the same object as `isLoggedIn` above: `save` reads
    // `localOverrideRef.current ?? show.isBookmarked`, which is correct here
    // because a not-yet-logged-in session can't have a true override or a
    // true prop — a freshly linked account can't already have bookmarked
    // this show.
    if (!isLoggedIn) {
      openLoginModal({
        origin,
        // The login modal fires its callback on ANY close, a dismissal
        // included, so re-read the real auth state: an Escape/outside-click
        // must not save on the anonymous device token (which the backend's
        // @authenticated accepts) and report a `save series` the user declined.
        callback: () => {
          if (useAuthStore.getState().currentUser?.providers.length) save();
        },
      });
      return;
    }
    save();
  };

  return {
    toggle,
    pending,
    // The reconciled state, so callers render what the hook already knows
    // instead of re-deriving the prop and lagging a cache broadcast behind.
    isBookmarked: localOverrideRef.current ?? isBookmarkedProp,
  };
}
